import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { AlertTriangle, FileText, Trash2, RefreshCw, Database, Cloud, Users, Target, CheckCircle, Archive } from 'lucide-react';

const S = {
  page: { padding: 0, color: '#e2e8f0' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 20, fontWeight: 700, color: '#f6c90e', display: 'flex', alignItems: 'center', gap: 10 },
  note: { fontSize: 11, color: '#475569', marginTop: 2 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 },
  card: { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 10, padding: '14px 16px' },
  cardLabel: { fontSize: 11, color: '#64748b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 },
  cardVal: { fontSize: 24, fontWeight: 700, color: '#e2e8f0' },
  cardSub: { fontSize: 11, color: '#475569', marginTop: 2 },
  section: { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 10, padding: '16px 20px', marginBottom: 16 },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: '#cbd5e1', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 },
  barBg: { background: '#1a2035', borderRadius: 99, height: 10, overflow: 'hidden', marginBottom: 6 },
  barFill: (pct, warn) => ({ height: 10, borderRadius: 99, width: `${Math.min(pct, 100)}%`, background: pct > 85 ? '#ef4444' : warn ? '#f59e0b' : '#3b82f6', transition: 'width 0.6s' }),
  barRow: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #1a2035', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  td: (i) => ({ padding: '10px 12px', borderBottom: '1px solid #0f1117', background: i % 2 === 0 ? '#0a0f1a' : 'transparent' }),
  badge: (status) => {
    const colors = {
      'Venda Fechada': { bg: '#064e3b', color: '#34d399' },
      'Desistiu': { bg: '#450a0a', color: '#f87171' },
      'Sem lead': { bg: '#1e1b4b', color: '#818cf8' },
    };
    const c = colors[status] || { bg: '#1a2035', color: '#94a3b8' };
    return { background: c.bg, color: c.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' };
  },
  btnPdf: { background: '#1D72B8', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 500 },
  btnDel: { background: '#7f1d1d', border: 'none', color: '#fca5a5', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 500 },
  btnConfirm: { background: '#ef4444', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 },
  btnCancel: { background: 'transparent', border: '1px solid #1a2035', color: '#64748b', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer' },
  btnRefresh: { background: 'transparent', border: '1px solid #1a2035', color: '#64748b', borderRadius: 6, padding: '6px 10px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 },
  actions: { display: 'flex', gap: 6, alignItems: 'center' },
  empty: { textAlign: 'center', padding: '40px 0', color: '#475569', fontSize: 14 },
  loadingRow: { textAlign: 'center', padding: '32px 0', color: '#475569' },
};

const R2_LIMIT_MB = 10240; // 10 GB em MB

export default function Monitoramento() {
  const [stats, setStats] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [geoFalhou, setGeoFalhou] = useState(null); // lista de clientes sem coords
  const [geoEdits, setGeoEdits] = useState({}); // {id: {endereco, cidade}}
  const [geoSaving, setGeoSaving] = useState({}); // id do cliente
  const [deletingId, setDeletingId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const [sRes, cRes] = await Promise.all([
        api.get('/admin/monitoramento/'),
        api.get('/admin/clientes-antigos/'),
      ]);
      setStats(sRes.data);
      setClientes(cRes.data);
    } catch {
      setErro('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleExportarPdf = async (clienteId, nome) => {
    setPdfLoadingId(clienteId);
    try {
      const res = await api.get(`/admin/clientes/${clienteId}/exportar-pdf/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nome.replace(/\s+/g, '_')}_completo.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleExcluir = async (clienteId) => {
    setDeletingId(clienteId);
    try {
      await api.delete(`/admin/clientes/${clienteId}/excluir/`);
      setClientes(prev => prev.filter(c => c.id !== clienteId));
      setConfirmDelete(null);
      if (stats) {
        setStats(prev => ({ ...prev, total_clientes: prev.total_clientes - 1, clientes_antigos_count: prev.clientes_antigos_count - 1 }));
      }
    } catch {
      alert('Erro ao excluir cliente.');
    } finally {
      setDeletingId(null);
    }
  };

  const storagePct = stats ? (stats.storage_estimado_mb / R2_LIMIT_MB) * 100 : 0;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}><Database size={20} /> Monitoramento</div>
          <div style={S.note}>* Valores de storage são aproximados com base na contagem de arquivos</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={{ ...S.btnRefresh, background:'rgba(96,165,250,0.12)', border:'1px solid rgba(96,165,250,0.3)', color:'#60a5fa' }}
            onClick={async () => {
              if (!window.confirm('Geocodificar todos os clientes sem coordenadas? Pode demorar alguns minutos (1 req/s).')) return;
              try {
                const r = await api.post('/admin/geocodificar-clientes/');
                const falhou = r.data.sem_coords || [];
                if (falhou.length === 0) {
                  alert(`Concluído! ${r.data.atualizados} clientes geocodificados. Nenhum pendente.`);
                  setGeoFalhou(null);
                } else {
                  setGeoFalhou(falhou);
                  const edits = {};
                  falhou.forEach(cli => { edits[cli.id] = { endereco: cli.endereco, cidade: cli.cidade, estado: cli.estado }; });
                  setGeoEdits(edits);
                  alert(`Concluído! ${r.data.atualizados} geocodificados. ${falhou.length} ainda sem coordenadas — veja a tabela abaixo.`);
                }
              } catch { alert('Erro ao geocodificar.'); }
            }}>
            📍 Geocodificar Mapa
          </button>
          <button style={S.btnRefresh} onClick={carregar} disabled={loading}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Tabela de clientes sem geocodificação */}
      {geoFalhou && geoFalhou.length > 0 && (
        <div style={{ background:'#0e1420', border:'1px solid rgba(96,165,250,0.3)', borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#60a5fa' }}>
              📍 Clientes sem coordenadas ({geoFalhou.length})
            </div>
            <button onClick={() => setGeoFalhou(null)}
              style={{ background:'none', border:'none', color:'#475569', cursor:'pointer', fontSize:18 }}>✕</button>
          </div>
          <div style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>
            Corrija o endereço/cidade e clique em Salvar para tentar novamente.
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ borderCollapse:'collapse', width:'100%', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#0b111e' }}>
                  {['Cliente','Telefone','Endereço','Cidade',''].map(h => (
                    <th key={h} style={{ padding:'8px 12px', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, textAlign:'left', borderBottom:'1px solid #1a2035', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {geoFalhou.map(cli => {
                  const edit = geoEdits[cli.id] || { endereco: cli.endereco, cidade: cli.cidade, estado: cli.estado };
                  const saving = !!geoSaving[cli.id];
                  return (
                    <tr key={cli.id} style={{ borderBottom:'1px solid #0f1117' }}>
                      <td style={{ padding:'8px 12px', color:'#e2e8f0', fontWeight:600, whiteSpace:'nowrap' }}>{cli.nome}</td>
                      <td style={{ padding:'8px 12px', color:'#94a3b8', whiteSpace:'nowrap' }}>{cli.telefone || '—'}</td>
                      <td style={{ padding:'8px 12px' }}>
                        <input
                          value={edit.endereco || ''}
                          onChange={e => setGeoEdits(prev => ({ ...prev, [cli.id]: { ...edit, endereco: e.target.value } }))}
                          style={{ background:'#0f1117', border:'1px solid #1a2035', borderRadius:6, color:'#e2e8f0', fontSize:12, padding:'5px 9px', outline:'none', width:200 }}
                          placeholder="Endereço"
                        />
                      </td>
                      <td style={{ padding:'8px 12px' }}>
                        <input
                          value={edit.cidade || ''}
                          onChange={e => setGeoEdits(prev => ({ ...prev, [cli.id]: { ...edit, cidade: e.target.value } }))}
                          style={{ background:'#0f1117', border:'1px solid #1a2035', borderRadius:6, color:'#e2e8f0', fontSize:12, padding:'5px 9px', outline:'none', width:140 }}
                          placeholder="Cidade"
                        />
                      </td>
                      <td style={{ padding:'8px 12px' }}>
                        <button
                          disabled={saving}
                          onClick={async () => {
                            setGeoSaving(prev => ({ ...prev, [cli.id]: true }));
                            try {
                              const r = await api.post(`/admin/geocodificar-cliente/${cli.id}/`, edit);
                              if (r.data.ok) {
                                setGeoFalhou(prev => prev.filter(x => x.id !== cli.id));
                              } else {
                                alert(`${cli.nome}: ${r.data.msg}`);
                              }
                            } catch { alert('Erro ao salvar.'); }
                            finally { setGeoSaving(prev => ({ ...prev, [cli.id]: false })); }
                          }}
                          style={{ padding:'5px 14px', borderRadius:6, background: saving ? '#1a2035' : 'rgba(96,165,250,0.15)', border:'1px solid rgba(96,165,250,0.35)', color: saving ? '#475569' : '#60a5fa', fontSize:12, fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
                          {saving ? 'Salvando...' : '📍 Salvar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cards de stats */}
      <div style={S.grid4}>
        <div style={S.card}>
          <div style={S.cardLabel}><Users size={14} /> Clientes</div>
          <div style={S.cardVal}>{loading ? '—' : stats?.total_clientes ?? 0}</div>
          <div style={S.cardSub}>{stats?.clientes_antigos_count ?? 0} com +1 ano</div>
        </div>
        <div style={S.card}>
          <div style={S.cardLabel}><Target size={14} /> Leads Ativos</div>
          <div style={S.cardVal}>{loading ? '—' : stats?.leads_ativos ?? 0}</div>
          <div style={S.cardSub}>em aberto</div>
        </div>
        <div style={S.card}>
          <div style={S.cardLabel}><CheckCircle size={14} /> Vendas Fechadas</div>
          <div style={S.cardVal}>{loading ? '—' : stats?.vendas_fechadas ?? 0}</div>
          <div style={S.cardSub}>total histórico</div>
        </div>
        <div style={S.card}>
          <div style={S.cardLabel}><Archive size={14} /> Arquivos</div>
          <div style={S.cardVal}>{loading ? '—' : stats?.total_arquivos ?? 0}</div>
          <div style={S.cardSub}>no storage</div>
        </div>
      </div>

      {/* Storage R2 */}
      <div style={S.section}>
        <div style={S.sectionTitle}><Cloud size={16} /> Armazenamento R2 (Cloudflare) — aproximado</div>
        <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#94a3b8' }}>
          <span>Uso estimado</span>
          <span style={{ fontWeight: 600, color: '#e2e8f0' }}>
            {loading ? '—' : `${((stats?.storage_estimado_mb ?? 0) / 1024).toFixed(2)} GB de 10 GB`}
          </span>
        </div>
        <div style={S.barBg}>
          <div style={S.barFill(storagePct, storagePct > 60)} />
        </div>
        <div style={S.barRow}>
          <span>{storagePct.toFixed(1)}% utilizado</span>
          <span>{((R2_LIMIT_MB - (stats?.storage_estimado_mb ?? 0)) / 1024).toFixed(2)} GB livres</span>
        </div>
      </div>

      {/* Tabela clientes antigos */}
      <div style={S.section}>
        <div style={S.sectionTitle}>
          <AlertTriangle size={16} color="#f59e0b" />
          Clientes com mais de 1 ano de cadastro ({clientes.length})
        </div>

        {erro && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{erro}</div>}

        {loading ? (
          <div style={S.loadingRow}>Carregando...</div>
        ) : clientes.length === 0 ? (
          <div style={S.empty}>Nenhum cliente com mais de 1 ano encontrado.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Nome</th>
                  <th style={S.th}>Cadastrado em</th>
                  <th style={S.th}>Vendedor</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c, i) => (
                  <tr key={c.id}>
                    <td style={S.td(i)}>
                      <span style={{ fontWeight: 500, color: '#e2e8f0' }}>{c.nome}</span>
                    </td>
                    <td style={S.td(i)}><span style={{ color: '#94a3b8' }}>{c.criado_em}</span></td>
                    <td style={S.td(i)}><span style={{ color: '#94a3b8' }}>{c.vendedor}</span></td>
                    <td style={S.td(i)}><span style={S.badge(c.status_lead)}>{c.status_lead}</span></td>
                    <td style={S.td(i)}>
                      {confirmDelete === c.id ? (
                        <div style={S.actions}>
                          <button
                            style={S.btnConfirm}
                            onClick={() => handleExcluir(c.id)}
                            disabled={deletingId === c.id}
                          >
                            <Trash2 size={13} />
                            {deletingId === c.id ? 'Excluindo...' : 'Confirmar exclusão'}
                          </button>
                          <button style={S.btnCancel} onClick={() => setConfirmDelete(null)}>Cancelar</button>
                        </div>
                      ) : (
                        <div style={S.actions}>
                          <button
                            style={S.btnPdf}
                            onClick={() => handleExportarPdf(c.id, c.nome)}
                            disabled={pdfLoadingId === c.id}
                            title="Gerar PDF com todos os dados e arquivos do cliente"
                          >
                            <FileText size={13} />
                            {pdfLoadingId === c.id ? 'Gerando...' : 'Guardar em PDF'}
                          </button>
                          <button
                            style={S.btnDel}
                            onClick={() => setConfirmDelete(c.id)}
                            title="Excluir cliente e todos os seus arquivos"
                          >
                            <Trash2 size={13} /> Excluir
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}
